import { type ReactNode } from 'react'
import { createApi } from '@reduxjs/toolkit/query/react'
import { BaseRequest } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { type Nullable } from '../../common/Nullable'

import { parseJwt } from '../../common/jwt'
import { INSIGHTS_CACHE_TTL_SECONDS } from '../../views/Insights/insightsUtils'

const _BASE_URL = import.meta.env.REACT_APP_INSIGHTS_API_BASE_URL!
const _AUTH_BASE_URL = import.meta.env.REACT_APP_AUTH_API_BASE_URL!

export type Dashboard = {
  id: string
  name: string
  domain: string
  token: string
}

export type RawBiDashboardsResponse = {
  biToken: string
  dashboards: Dashboard[]
}

export type BiDashboardsResponse = {
  dashboards: Dashboard[]
}

export type DashboardFilter = {
  filterKey: string
  filterValue: string
  filterOperator: string
}

export interface DashboardRequest extends BaseRequest {
  filters?: Nullable<DashboardFilter[]>
  dashboardIds?: Nullable<string[]>
}

// ── Highlight types ──

export type TopicHighlight = {
  topic: string
  detail: ReactNode
}

export type MarketOutlookHighlight = {
  optimisticPercent: number
  neutralPercent: number
  concernedPercent: number
  summaryText: string
  strongestOpportunity: string
  topConcern: string
}

export type HeldAwayHighlight = {
  suggestedPercent: number
  agreedPercent: number
  summaryText: string
  leadMeetingType: string
  bestConvertMeetingType: string
}

export type ReferralHighlight = {
  commitRate: number
  summaryText: string
  leadAskMeetingType: string
  leadProvidedMeetingType: string
}

export type Highlights = {
  meetingCount: number
  mostDiscussed: TopicHighlight | null
  mostClientDriven: TopicHighlight | null
  mostTrending: TopicHighlight | null
  mostQuestionsRaised: TopicHighlight | null
  marketOutlook: MarketOutlookHighlight | null
  heldAwayAssets: HeldAwayHighlight | null
  referrals: ReferralHighlight | null
}

// ── Discussion Topics types ──

export type TrendDirection = 'up' | 'down' | 'neutral' | null

export type DiscussionTopicRow = {
  topic: string
  mentions: number
  clientDrivenPercent: number
  trendValue: number | null
  trendDirection: TrendDirection
  questionsRaised: number
}

export type InvestmentProductRow = {
  topic: string
  subTopic: string
  mentions: number
  clientDrivenPercent: number
  trendValue: number | null
  trendDirection: TrendDirection
  questionsRaised: number
}

// ── Client Outlook types ──

export type AssetClassOutlookRow = {
  assetClass: string
  optimisticPercent: number
  optimisticBarWidth: number
  concernedPercent: number
  concernedBarWidth: number
}

export type ClientOutlook = {
  optimisticRows: AssetClassOutlookRow[]
  concernedRows: AssetClassOutlookRow[]
  totalAssetClasses: number
}

// ── Asset Opportunities types ──

export type AssetOpportunityMetrics = {
  assetMentions: number
  assetMentionsTrend: number
  assetMentionsLeadMeetingType: string
  suggestionsMade: number
  suggestionsMadeTrend: number
  suggestionsLeadMeetingType: string
  clientAgreed: number
  clientAgreedTrend: number
  clientAgreedLeadMeetingType: string
}

export type AssetOpportunityRow = {
  meetingType: string
  assetMentions: number
  assetMentionsTrend: number
  suggestionMade: number
  suggestionMadePct: number
  suggestionMadeTrend: number
  clientAgreed: number
  clientAgreedPct: number
  clientAgreedTrend: number
}

export type AssetOpportunities = {
  metrics: AssetOpportunityMetrics
  rows: AssetOpportunityRow[]
}

// ── Referrals types ──

export type ReferralMetrics = {
  referralsAsked: number
  referralsAskedTrend: number
  referralsAskedLeadMeetingType: string
  referralsProvided: number
  referralsProvidedTrend: number
  referralsProvidedLeadMeetingType: string
}

export type ReferralRow = {
  meetingType: string
  meetings: number
  referralsAsked: number
  referralsAskedTrend: number
  referralsProvided: number
  referralsProvidedTrend: number
}

export type Referrals = {
  metrics: ReferralMetrics
  rows: ReferralRow[]
  isPartialData: boolean
}

// ── Dashboard data ──

export type InsightsDashboardData = {
  timeRange: string
  highlights: Highlights
  discussionTopics: DiscussionTopicRow[]
  investmentProducts: InvestmentProductRow[]
  clientOutlook: ClientOutlook | null
  assetOpportunities: AssetOpportunities | null
  referrals: Referrals | null
}

// ── API types ──

export interface InsightsDataRequest extends BaseRequest {
  from: string
  to: string
  userIdList?: string[]
}

export type TopicStatRow = {
  topic: string
  total_meetings_current: number
  total_meetings_previous: number
  meetings_with_topic_current: number
  meetings_with_topic_previous: number
  total_mentions_current: number
  total_mentions_previous: number
  questions_current: number
  questions_previous: number
  client_driven_pct_current: number
  client_driven_meetings_current: number
  client_driven_meetings_previous: number
  client_driven_pct_point_change: number
}

export type InvestmentProductStatRow = {
  sub_topic: string | null
  total_meetings_current: number
  total_meetings_previous: number
  meetings_with_subtopic_current: number
  meetings_with_subtopic_previous: number
  total_mentions_current: number
  total_mentions_previous: number
  questions_current: number
  questions_previous: number
  client_driven_pct_current: number
  client_driven_meetings_current: number
  client_driven_meetings_previous: number
  client_driven_pct_point_change: number
}

export type InsightsDataCharts = {
  client_outlook?: Array<{
    asset_class: string
    optimistic: number
    neutral: number
    concerned: number
    total_count: number
    bullish_count: number
    neutral_count: number
    bearish_count: number
  }>
  referrals?: Array<{
    meeting_type: string
    meetings_current: number
    referrals_asked_current: number
    referrals_asked_previous: number
    referrals_asked_trend: number
    referrals_provided_current: number
    referrals_provided_previous: number
    referrals_provided_trend: number
  }>
  held_away_assets?: Array<{
    template_name: string
    asset_mentioned_current: number
    asset_mentioned_previous: number
    asset_mentioned_trend: number
    suggestion_made_current: number
    suggestion_made_previous: number
    suggestion_made_trend: number
    client_agreed_current: number
    client_agreed_previous: number
    client_agreed_trend: number
  }>
  topic_stats?: TopicStatRow[]
  investment_product_stats?: InvestmentProductStatRow[]
}

export type InsightsDataApiResponse = {
  from?: string
  to?: string
  charts?: InsightsDataCharts
}

export const insightsApi = createApi({
  reducerPath: 'insightsApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['Insights'],
  endpoints: (builder) => ({
    getInsightsData: builder.query<InsightsDataApiResponse, InsightsDataRequest>({
      query: ({ accountId, from, to, userIdList }) => ({
        url: `/v1/accounts/${accountId}/insights/query`,
        method: 'POST',
        body: {
          from,
          to,
          ...(userIdList?.length ? { userIdList } : {}),
        },
      }),
      transformResponse: (response: { data?: InsightsDataApiResponse } & InsightsDataApiResponse) => response.data ?? response,
      keepUnusedDataFor: INSIGHTS_CACHE_TTL_SECONDS,
    }),
    getBiDashboards: builder.query<Dashboard[], DashboardRequest>({
      queryFn: async (request, api, extraOptions) => {
        const { accountId, filters, dashboardIds } = request
        const authBaseQuery = baseQueryWithTokenRefresh(_AUTH_BASE_URL)
        const result = await authBaseQuery(
          {
            url: `/v1/accounts/${accountId}/bi-dashboards`,
            method: 'POST',
            body: {
              filters: filters,
              internalIds: dashboardIds,
            },
          },
          api,
          extraOptions,
        )
        if (result.error) return { error: result.error }
        const { dashboards, biToken } = result.data as RawBiDashboardsResponse
        const token = parseJwt(biToken)
        return {
          data: dashboards.map((d) => ({
            id: d.id,
            name: d.name,
            domain: token.aud,
            token: biToken,
          })),
        }
      },
    }),
  }),
})

export const { useGetBiDashboardsQuery, useGetInsightsDataQuery } = insightsApi
